import {
  DataManagerPaginatorPosition,
  type DataTableItem,
  type FormConfig,
  FormDefaultAttributes,
  type PartialDataManagerConfig,
  type PartialDataManagerPaginatorConfig,
  type PartialDataTableConfig,
  type SelectConfig,
  type TextConfig
} from '@common';
import type { StatisticsItem } from '../models';
import { markRaw } from 'vue';
import StatisticsDataTableCreatedAtCell from '../components/StatisticsDataTableCreatedAtCell.vue';
import { StatisticsDataTableParcelsCell, StatisticsDataTableStatusCell } from '../components';
import { getStatisticsKindFilterOptions } from '../utils';
import { StatisticsStatus } from '../enums';

export const STATISTICS_TABLE_CONFIG: PartialDataTableConfig = {
  needTableHeader: true,
  showViewModes: false,
  fixedHeader: true,
  showIndexColumn: false,
  mobileBreakpoint: 0,
  clickableItems: false,
  height: '100%',
  hover: true,
  columns: [
    {
      name: 'kind',
      label: 'statistics.label.statisticsKind',
      translateLabel: true,
      class: 'col-statisticsType',
      cellConfig: {
        translatePrefix: 'statistics.kind.',
        translate: true
      }
    },
    {
      name: 'name',
      label: 'statistics.label.name',
      translateLabel: true,
      class: 'col-statisticsName'
    },
    {
      name: 'parcels',
      label: 'statistics.label.parcelNames',
      translateLabel: true,
      class: 'col-statisticsParcelNames',
      cellComponent: markRaw(StatisticsDataTableParcelsCell)
    },
    {
      name: 'year',
      label: 'statistics.label.year',
      translateLabel: true,
      class: 'col-statisticsYear'
    },
    {
      name: 'createdAt',
      label: 'statistics.label.createdAt',
      translateLabel: true,
      class: 'col-statisticsCreatedAt',
      cellComponent: markRaw(StatisticsDataTableCreatedAtCell)
    },
    {
      name: 'status',
      label: 'statistics.label.statisticsStatus',
      translateLabel: true,
      class: 'col-statisticsStatus',
      cellConfig: {
        emptyValue: '-'
      },
      cellComponent: markRaw(StatisticsDataTableStatusCell)
    }
  ],
  actions: {
    global: [],
    row: [
      {
        action: 'details',
        actionConfig: {
          label: 'button.details',
          translateLabel: true,
          icon: 'mdi-eye',
          size: 'small',
          variant: 'flat'
        },
        disabledCondition: (item: DataTableItem<StatisticsItem>) =>
          [StatisticsStatus.ERROR, StatisticsStatus.GENERATING].includes(item.data.status)
      },
      {
        action: 'copy',
        actionConfig: {
          label: 'button.copy',
          translateLabel: true,
          icon: 'mdi-content-copy',
          size: 'small',
          variant: 'flat'
        },
        disabledCondition: (item: DataTableItem<StatisticsItem>) => item.data.status === StatisticsStatus.GENERATING
      },
      {
        action: 'delete',
        actionConfig: {
          label: 'button.delete',
          translateLabel: true,
          icon: 'mdi-delete',
          size: 'small',
          variant: 'flat',
          iconColor: 'error'
        },
        disabledCondition: (item: DataTableItem<StatisticsItem>) => item.data.status === StatisticsStatus.GENERATING
      }
    ]
  }
};

export const STATISTICS_FILTER_FORM_CONFIG: FormConfig = {
  name: 'statisticsTableFiltersForm',
  attributes: {
    ...FormDefaultAttributes.getFormDefaultAttributes(),
    class: 'flex-grow-1 z-5'
  },
  settings: {},
  children: [
    {
      name: 'searchText',
      component: 'text',
      attributes: {
        type: 'text',
        label: 'filter.search',
        hideDetails: true
      },
      settings: {
        grid: { cols: 12, md: 4 },
        needTranslate: true
      }
    } as TextConfig,
    {
      component: 'select',
      name: 'kind',
      attributes: {
        label: 'filter.type',
        hideDetails: true,
        multiple: false,
        itemTitle: 'name',
        clearable: true
      },
      settings: {
        needTranslate: true,
        needItemTranslate: true,
        grid: { cols: 12, md: 4 },
        items: getStatisticsKindFilterOptions()
      }
    } as SelectConfig,
    {
      component: 'select',
      name: 'year',
      attributes: {
        label: 'statistics.label.year',
        hideDetails: true,
        multiple: false,
        clearable: true,
        itemTitle: 'name',
        itemValue: 'value'
      },
      settings: {
        needTranslate: true,
        grid: { cols: 12, md: 4 }
      }
    } as SelectConfig
  ]
};

export const STATISTICS_PAGINATOR_CONFIG: PartialDataManagerPaginatorConfig = {
  totalVisible: 7,
  itemsPerPage: [20, 50, 100],
  defaultPageSize: 50,
  rounded: true,
  position: DataManagerPaginatorPosition.BOTTOM
};

export const STATISTICS_DATA_MANAGER_CONFIG: PartialDataManagerConfig = {
  name: 'statisticsDataManager',
  dataTableConfig: STATISTICS_TABLE_CONFIG,
  filters: {
    filterDelay: 500
  },
  paginator: STATISTICS_PAGINATOR_CONFIG
};
